import React from "react";
import { Link } from "react-router-dom";
import {
  MapPin,
  FileText,
  ScrollText,
  HeartPulse,
  Briefcase,
  Clock,
  Moon,
  ArrowUpRight,
  Home,
} from "lucide-react";
import { PageHero, GoldButton, SectionEyebrow, FadedBackdrop } from "@/components/ui-bits";
import { IMG } from "@/constants/images";

const MOBILE_IMG =
  "https://images.pexels.com/photos/8730998/pexels-photo-8730998.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const DOCS = [
  {
    icon: FileText,
    title: "Power of Attorney",
    body: "Durable, limited, springing, and healthcare POAs notarized at your location with proper acknowledgement and witnesses where required.",
  },
  {
    icon: ScrollText,
    title: "Wills & Trusts",
    body: "Last wills, living trusts, trust amendments, and certifications of trust. We coordinate witnesses on site when applicable.",
  },
  {
    icon: HeartPulse,
    title: "Healthcare Directives",
    body: "Living wills, healthcare surrogates, HIPAA releases, and Do-Not-Resuscitate forms — including hospital and care-facility appointments.",
  },
  {
    icon: Briefcase,
    title: "Business Contracts",
    body: "Affidavits, LLC operating agreements, vendor contracts, employment documents, vehicle titles, and acknowledgements for business signers.",
  },
];

const AREAS = [
  "Coastal Florida",
  "County-Wide Coverage",
  "Hospitals & Care Facilities",
  "Senior Living & Assisted Care",
  "Office, Home & Job Sites",
  "After-Hours by Appointment",
];

const BENEFITS = [
  {
    icon: Home,
    title: "We Come to You",
    body: "Home, office, hospital, coffee shop, jobsite — wherever the document needs to be signed. You don't waste a half-day on the road.",
  },
  {
    icon: Moon,
    title: "Nights & Weekends",
    body: "We schedule around our clients' needs. Evening and weekend appointments are standard, not exceptions.",
  },
  {
    icon: Clock,
    title: "Same-Day & Rush",
    body: "Time-sensitive document? We accept same-day and rush appointments when our schedule allows, with up-front pricing.",
  },
];

export default function MobileNotaryPage() {
  return (
    <>
      <PageHero
        eyebrow="Mobile Notary"
        title="We come to you. Days, nights, weekends — at your service."
        lead="Local mobile notary service for individuals, families, and businesses across coastal Florida. Whether it's a kitchen-table will signing, a hospital-bedside healthcare directive, or a same-day business contract, we arrive prepared, professional, and on time."
        backdrop={IMG.mobileVisit}
        backdropOpacity={0.6}
        bright
      >
        <div className="flex flex-wrap gap-4">
          <GoldButton
            as={Link}
            to="/contact"
            data-testid="mobile-notary-book-btn"
          >
            Schedule a Visit <ArrowUpRight className="w-4 h-4" />
          </GoldButton>
        </div>
      </PageHero>

      {/* Doc types */}
      <section className="py-24 sm:py-32 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="max-w-3xl mb-14">
            <SectionEyebrow className="mb-4">Common Documents</SectionEyebrow>
            <h2 className="font-serif-display text-4xl sm:text-5xl text-white tracking-tight leading-tight">
              The everyday documents we handle on-site.
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-6 lg:gap-8">
            {DOCS.map((d, i) => (
              <div
                key={i}
                data-testid={`mobile-doc-${i}`}
                className="border border-white/10 bg-[#1A2B45] p-8 hover:border-[#D4AF37]/50 transition-colors"
              >
                <div className="w-12 h-12 border border-[#D4AF37]/40 flex items-center justify-center text-[#D4AF37] mb-6">
                  <d.icon className="w-6 h-6" />
                </div>
                <h3 className="font-serif-display text-2xl text-white mb-3">
                  {d.title}
                </h3>
                <p className="text-slate-300 text-sm leading-relaxed">
                  {d.body}
                </p>
              </div>
            ))}
          </div>

          <p className="text-slate-500 text-xs leading-relaxed mt-8 max-w-3xl">
            * A Notary Public is not an attorney and cannot give legal advice,
            draft documents, or interpret content. Please bring completed
            documents and consult a licensed attorney for legal guidance.
          </p>
        </div>
      </section>

      {/* Service area + image */}
      <section className="py-24 sm:py-32 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6">
            <SectionEyebrow className="mb-4">Service Area</SectionEyebrow>
            <h2 className="font-serif-display text-4xl text-white leading-tight">
              Coastal Florida coverage, on call when you need us.
            </h2>
            <p className="mt-6 text-slate-300 leading-relaxed">
              Our primary service area is coastal Florida and the surrounding
              county. We routinely travel to homes, offices, hospitals, senior
              living facilities, and job sites within our coverage zone.
              Out-of-area travel is available — just ask for a quote.
            </p>
            <ul className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3">
              {AREAS.map((a, i) => (
                <li
                  key={i}
                  data-testid={`mobile-area-${i}`}
                  className="flex items-center gap-3 border border-white/10 px-5 py-4 bg-[#1A2B45]"
                >
                  <MapPin className="w-4 h-4 text-[#D4AF37] shrink-0" />
                  <span className="text-slate-200 text-sm">{a}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="lg:col-span-6">
            <div
              className="relative border border-white/10 aspect-[5/4] bg-cover bg-center"
              style={{ backgroundImage: `url(${MOBILE_IMG})` }}
            >
              <div className="absolute inset-0 bg-gradient-to-t from-[#142339] via-transparent to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-6 border-t border-white/10 bg-[#142339]/80 backdrop-blur-sm">
                <div className="eyebrow mb-2">On The Road</div>
                <p className="font-serif-display text-xl text-white">
                  Travel built into the service — not an after-thought.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="relative overflow-hidden py-24 sm:py-32">
        <FadedBackdrop src={IMG.signingTable} opacity={0.22} />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-12">
          <div className="max-w-3xl mb-14">
            <SectionEyebrow className="mb-4">Why Clients Choose Us</SectionEyebrow>
            <h2 className="font-serif-display text-4xl sm:text-5xl text-white tracking-tight leading-tight">
              Scheduled around your life — not ours.
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
            {BENEFITS.map((b, i) => (
              <div
                key={i}
                data-testid={`mobile-benefit-${i}`}
                className="border border-white/10 bg-[#1A2B45] p-8 hover:border-[#D4AF37]/50 transition-colors"
              >
                <div className="w-12 h-12 border border-[#D4AF37]/40 flex items-center justify-center text-[#D4AF37] mb-6">
                  <b.icon className="w-6 h-6" />
                </div>
                <h3 className="font-serif-display text-2xl text-white mb-3">
                  {b.title}
                </h3>
                <p className="text-slate-300 text-sm leading-relaxed">
                  {b.body}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
